import { useMemo, useState, type ReactNode } from 'react'
import {
  BadgeStatus,
  Button,
  ConfirmDialog,
  DataTable,
  FilterBar,
  Input,
  KolomForm,
  Modal,
  PageHeader,
  Select,
  Textarea,
  FILTER_KOSONG,
  type KolomTabel,
  type NilaiFilter,
} from '@/components'
import { api, buatIdempotencyKey, pesanKesalahan } from '@/lib/api'
import { formatRupiah, formatTanggal } from '@/lib/format'
import type { KasKecil } from '@/lib/types'
import { useApi, usePaged, useAksi } from '@/lib/useApi'
import { useOpsiUnit } from '@/lib/opsi'
import { useAuth } from '@/lib/auth'

const STATUS = ['DRAFT', 'SUBMITTED', 'REVIEWED', 'APPROVED', 'DISBURSED', 'USED', 'ACCOUNTED', 'VERIFIED', 'COMPLETED', 'REJECTED', 'NEEDS_REVISION', 'CANCELLED', 'OVERDUE']

export default function KasKecilPage() {
  const { punyaIzin } = useAuth()
  const opsiUnit = useOpsiUnit()
  const aksi = useAksi()
  const [filter, setFilter] = useState<NilaiFilter>(FILTER_KOSONG)
  const [statusFilter, setStatusFilter] = useState('')
  const [modal, setModal] = useState(false)
  const [detail, setDetail] = useState<KasKecil | null>(null)
  const [konfirmasi, setKonfirmasi] = useState<{ aksi: 'submit' | 'review' | 'approve' | 'reject'; data: KasKecil } | null>(null)

  const daftar = usePaged<KasKecil>('/petty-cash', {
    query: { unitId: filter.unitId || undefined, status: statusFilter || undefined },
  })

  const jalankan = async (id: string, tindakan: string, alasan?: string) => {
    const hasil = await aksi.jalankan(
      () =>
        api.post(
          `/petty-cash/${id}/${tindakan}`,
          tindakan === 'reject' ? { reason: alasan } : {},
          { idempotencyKey: buatIdempotencyKey() },
        ),
      { sukses: `Pengajuan kas kecil berhasil di-${tindakan}.` },
    )
    if (hasil !== null) {
      setKonfirmasi(null)
      daftar.muat()
    }
  }

  const kolom = useMemo<KolomTabel<KasKecil>[]>(
    () => [
      {
        kunci: 'requestNumber',
        judul: 'Nomor',
        render: (b) => (
          <button type="button" className="font-medium text-yayasan-800 hover:underline" onClick={() => setDetail(b)}>
            {b.requestNumber}
          </button>
        ),
      },
      { kunci: 'requestDate', judul: 'Tanggal', render: (b) => formatTanggal(b.requestDate) },
      { kunci: 'purpose', judul: 'Keperluan' },
      { kunci: 'unit', judul: 'Unit', render: (b) => b.unit?.name ?? '—' },
      { kunci: 'requestedAmount', judul: 'Diminta', rata: 'kanan', render: (b) => formatRupiah(b.requestedAmount) },
      { kunci: 'approvedAmount', judul: 'Disetujui', rata: 'kanan', render: (b) => formatRupiah(b.approvedAmount) },
      { kunci: 'disbursedAmount', judul: 'Dicairkan', rata: 'kanan', render: (b) => formatRupiah(b.disbursedAmount) },
      { kunci: 'status', judul: 'Status', render: (b) => <BadgeStatus status={b.status} /> },
      {
        kunci: 'aksi',
        judul: 'Aksi',
        sortable: false,
        render: (b) => (
          <div className="flex flex-wrap gap-1.5">
            <Button ukuran="kecil" varian="garis" onClick={() => setDetail(b)}>
              Detail
            </Button>
            {punyaIzin('pettycash:submit') && b.status === 'DRAFT' ? (
              <Button ukuran="kecil" onClick={() => setKonfirmasi({ aksi: 'submit', data: b })}>
                Ajukan
              </Button>
            ) : null}
            {punyaIzin('pettycash:review') && b.status === 'SUBMITTED' ? (
              <Button ukuran="kecil" varian="garis" onClick={() => setKonfirmasi({ aksi: 'review', data: b })}>
                Tinjau
              </Button>
            ) : null}
            {punyaIzin('pettycash:approve') && b.status === 'REVIEWED' ? (
              <Button ukuran="kecil" varian="sekunder" onClick={() => setKonfirmasi({ aksi: 'approve', data: b })}>
                Setujui
              </Button>
            ) : null}
            {punyaIzin('pettycash:review', 'pettycash:approve') &&
            ['SUBMITTED', 'REVIEWED'].includes(b.status) ? (
              <Button ukuran="kecil" varian="bahaya" onClick={() => setKonfirmasi({ aksi: 'reject', data: b })}>
                Tolak
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [punyaIzin],
  )

  return (
    <div>
      <PageHeader
        judul="Kas Kecil"
        deskripsi="Pengajuan, persetujuan, dan pencairan kas kecil dengan status bertahap."
        ekspor={{
          path: '/reports/fund-flow',
          namaDasar: 'kas-kecil',
          query: { unitId: filter.unitId || undefined },
        }}
        aksi={punyaIzin('pettycash:create') ? <Button onClick={() => setModal(true)}>+ Pengajuan Baru</Button> : null}
      />

      <FilterBar
        nilai={filter}
        onUbah={setFilter}
        unit={opsiUnit}
        tampilkan={{ unitId: true, dari: false, sampai: false, fundId: false, program: false, kategori: false }}
        ekstra={
          <Select
            label="Status"
            placeholder="Semua status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            opsi={STATUS.map((s) => ({ value: s, label: s }))}
            className="min-w-44"
          />
        }
      />

      <DataTable
        kolom={kolom}
        data={daftar.items}
        kunciBaris={(b) => b.id}
        memuat={daftar.memuat}
        galat={daftar.galat}
        kodeGalat={daftar.kodeGalat}
        onCoba={daftar.muat}
        meta={daftar.meta}
        onHalaman={daftar.setHalaman}
        pesanKosong="Belum ada pengajuan kas kecil."
      />

      <FormPengajuan terbuka={modal} onTutup={() => setModal(false)} setelah={daftar.muat} />
      <DetailKasKecil data={detail} onTutup={() => setDetail(null)} />

      <ConfirmDialog
        terbuka={konfirmasi !== null}
        onTutup={() => setKonfirmasi(null)}
        judul={`Konfirmasi ${konfirmasi?.aksi ?? ''}`}
        pesan={`Lanjutkan tindakan "${konfirmasi?.aksi}" untuk pengajuan ${konfirmasi?.data.requestNumber}?`}
        labelKonfirmasi="Lanjutkan"
        berbahaya={konfirmasi?.aksi === 'reject'}
        labelAlasan={konfirmasi?.aksi === 'reject' ? 'Alasan penolakan' : undefined}
        minimalAlasan={5}
        memuat={aksi.memuat}
        onKonfirmasi={(alasan) => {
          if (!konfirmasi) return
          void jalankan(konfirmasi.data.id, konfirmasi.aksi, alasan)
        }}
      />
    </div>
  )
}

function FormPengajuan({
  terbuka,
  onTutup,
  setelah,
}: {
  terbuka: boolean
  onTutup: () => void
  setelah: () => void
}) {
  const opsiUnit = useOpsiUnit()
  const tahun = useApi<unknown>('/fiscal-years', { perPage: 50 })
  const daftarTahun = Array.isArray(tahun.data)
    ? (tahun.data as Array<{ id: string; code: string; name: string; isCurrent: boolean }>)
    : ((tahun.data as { items?: Array<{ id: string; code: string; name: string; isCurrent: boolean }> } | null)?.items ?? [])

  const [unitId, setUnitId] = useState('')
  const [fiscalYearId, setFiscalYearId] = useState('')
  const [periodNumber, setPeriodNumber] = useState('1')
  const [requestDate, setRequestDate] = useState(new Date().toISOString().slice(0, 10))
  const [neededByDate, setNeededByDate] = useState('')
  const [purpose, setPurpose] = useState('')
  const [requestedAmount, setRequestedAmount] = useState('')
  const [notes, setNotes] = useState('')
  const [galat, setGalat] = useState<string | null>(null)
  const [memuat, setMemuat] = useState(false)

  // Tahun buku berjalan dipilih otomatis bila tersedia.
  const idTahunTerpilih = fiscalYearId || daftarTahun.find((t) => t.isCurrent)?.id || ''

  const simpan = async () => {
    setGalat(null)
    if (!unitId) return setGalat('Unit wajib dipilih.')
    if (!idTahunTerpilih) return setGalat('Tahun buku wajib dipilih.')
    if (!purpose.trim()) return setGalat('Keperluan wajib diisi.')
    const nilai = Number(requestedAmount.replace(/[^0-9.-]/g, ''))
    if (!nilai || nilai <= 0) return setGalat('Jumlah diajukan harus lebih dari nol.')

    setMemuat(true)
    try {
      await api.post(
        '/petty-cash',
        {
          unitId,
          fiscalYearId: idTahunTerpilih,
          periodNumber: Number(periodNumber) || 1,
          requestDate,
          neededByDate: neededByDate || undefined,
          purpose: purpose.trim(),
          requestedAmount: nilai,
          notes: notes.trim() || undefined,
        },
        { idempotencyKey: buatIdempotencyKey() },
      )
      setelah()
      onTutup()
    } catch (e) {
      setGalat(pesanKesalahan(e))
    } finally {
      setMemuat(false)
    }
  }

  return (
    <Modal
      terbuka={terbuka}
      onTutup={onTutup}
      judul="Pengajuan Kas Kecil"
      lebar="max-w-2xl"
      footer={
        <>
          <Button varian="garis" onClick={onTutup} disabled={memuat}>
            Batal
          </Button>
          <Button onClick={() => void simpan()} memuat={memuat}>
            Simpan Pengajuan
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {galat ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {galat}
          </div>
        ) : null}
        <KolomForm kolom={2}>
          <Select
            label="Unit"
            placeholder="Pilih unit"
            opsi={opsiUnit}
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
            required
          />
          <Select
            label="Tahun Buku"
            placeholder="Pilih tahun buku"
            opsi={daftarTahun.map((t) => ({ value: t.id, label: `${t.code} — ${t.name}` }))}
            value={idTahunTerpilih}
            onChange={(e) => setFiscalYearId(e.target.value)}
            required
            bantuan={tahun.galat ? `Gagal memuat tahun buku: ${tahun.galat}` : undefined}
          />
          <Input
            label="Periode ke-"
            type="number"
            min={1}
            max={12}
            value={periodNumber}
            onChange={(e) => setPeriodNumber(e.target.value)}
          />
          <Input
            type="date"
            label="Tanggal Pengajuan"
            value={requestDate}
            onChange={(e) => setRequestDate(e.target.value)}
            required
          />
          <Input
            type="date"
            label="Dibutuhkan Sebelum"
            value={neededByDate}
            onChange={(e) => setNeededByDate(e.target.value)}
          />
          <Input
            label="Jumlah Diajukan (Rp)"
            inputMode="numeric"
            value={requestedAmount}
            onChange={(e) => setRequestedAmount(e.target.value)}
            required
          />
        </KolomForm>
        <Textarea label="Keperluan" value={purpose} onChange={(e) => setPurpose(e.target.value)} required />
        <Textarea label="Catatan" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </div>
    </Modal>
  )
}

function DetailKasKecil({ data, onTutup }: { data: KasKecil | null; onTutup: () => void }) {
  const detail = useApi<KasKecil>(data ? `/petty-cash/${data.id}` : null)
  const isi = detail.data ?? data

  return (
    <Modal
      terbuka={data !== null}
      onTutup={onTutup}
      judul={isi ? `Pengajuan ${isi.requestNumber}` : 'Detail Pengajuan'}
      lebar="max-w-3xl"
      footer={
        <Button varian="garis" onClick={onTutup}>
          Tutup
        </Button>
      }
    >
      {detail.galat ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{detail.galat}</p>
      ) : null}
      {isi ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Info label="Status">
              <BadgeStatus status={isi.status} />
            </Info>
            <Info label="Tanggal">{formatTanggal(isi.requestDate)}</Info>
            <Info label="Unit">{isi.unit?.name ?? '—'}</Info>
            <Info label="Diminta">{formatRupiah(isi.requestedAmount)}</Info>
            <Info label="Disetujui">{formatRupiah(isi.approvedAmount)}</Info>
            <Info label="Dicairkan">{formatRupiah(isi.disbursedAmount)}</Info>
            <Info label="Terpakai">{formatRupiah(isi.usedAmount)}</Info>
            <Info label="Dikembalikan">{formatRupiah(isi.returnedAmount)}</Info>
            <Info label="Jatuh Tempo Pertanggungjawaban">{formatTanggal(isi.accountabilityDueDate ?? null)}</Info>
          </div>
          <div>
            <p className="text-xs font-medium tracking-wide text-netral-500 uppercase">Keperluan</p>
            <p className="text-sm text-netral-800">{isi.purpose}</p>
          </div>
          {(isi.lines ?? []).length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-netral-200">
              <table className="w-full text-sm">
                <thead className="bg-netral-50 text-left text-xs uppercase text-netral-600">
                  <tr>
                    <th className="px-3 py-2">Akun</th>
                    <th className="px-3 py-2">Uraian</th>
                    <th className="px-3 py-2 text-right">Jumlah</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-netral-100">
                  {(isi.lines ?? []).map((l) => (
                    <tr key={l.id}>
                      <td className="px-3 py-2">{l.account ? `${l.account.code} — ${l.account.name}` : l.accountId}</td>
                      <td className="px-3 py-2">{l.description}</td>
                      <td className="px-3 py-2 text-right">{formatRupiah(l.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-netral-500">Belum ada rincian baris pengajuan.</p>
          )}
        </div>
      ) : null}
    </Modal>
  )
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium tracking-wide text-netral-500 uppercase">{label}</p>
      <p className="mt-0.5 text-sm text-netral-800">{children}</p>
    </div>
  )
}
